/**
 * One draft: reviewed, then published — paused — or edited or discarded.
 *
 * ## PUBLISH NAMES THE VERSION ON THIS SCREEN
 *
 * The Publish button sends the `version` of the record this page rendered, and
 * the API claims the draft only at that version. If somebody edited it in
 * another tab after this page loaded, the publish is refused as a conflict and
 * the page says to reload — what reaches Meta is always what was on this
 * screen when the button was pressed.
 *
 * ## TWO PRESSES, AND THE SECOND ONE RESTATES WHAT, HOW MUCH, AND WHERE
 *
 * The first press opens a confirmation of four facts: the budget as money in
 * the account's currency, the end date or that there is none, the ads still to
 * be created, and the ad account they go into — what this campaign can spend
 * once slice 43 starts it, and whose money that is. The confirming button says
 * what it does: publish, paused. Nothing is active after it.
 *
 * A publish Meta stopped part way returns a `failed` record, not an error:
 * what it created is listed, the reason is printed, and pressing Publish again
 * resumes — so the confirmation then counts only what is still to be created,
 * and names the account the campaign already lives in. When that is not the
 * connected account the API will refuse, so the button is off and the account
 * says why.
 *
 * ## AND SINCE SLICE 52, IT CAN BE CORRECTED FIRST
 *
 * Publishing again unchanged is no use when what stopped the publish was the
 * draft — a budget under Meta's minimum for the currency is the commonest
 * refusal there is, and until this slice the only way past it was a new draft
 * beside a campaign already created. **Correct** opens the composer on the
 * parts that do not exist at Meta yet; `frozenAtMeta` decides which those are,
 * and the resume continues into the ids already on the record, so no second
 * campaign is ever created. The ids grid in `MetaAdDraftSummary` is what makes
 * that legible — it already draws exactly what exists — so nothing is added to
 * this page but the two controls.
 *
 * **Discard stays withdrawn, and Ads Manager takes its place.** A row whose
 * campaign exists at Meta is the only record of it, so the row is never
 * deleted; somebody who would rather delete the campaign than correct the
 * draft is sent to the place that can, on the account it is in.
 *
 * ## ONCE PUBLISHED, THE CONTROLS LEAD AND THE DRAFT FOLDS
 *
 * A published draft cannot be edited, discarded or published again, so the
 * publish actions give way to `MetaAdControls` (slice 43) — Meta's own reading
 * of the campaign, start, pause, budget, spend cap and the history of each —
 * and they come first: "is it running, and can I stop it" is the question on
 * this page now, and the brake two screens down under the ad copy was the
 * shape of a review page, not a control page. The composed draft folds below
 * them as the record of what was published: in the DOM, findable, and not
 * the first thing between a person and the Pause button.
 *
 * ## THEN WHAT IT HAS DONE, SURFACE BY SURFACE
 *
 * Slice 44: once the campaign has delivered, its lifetime results sit under
 * the controls — the whole, and a panel per surface (Facebook ads, Instagram
 * ads, and WhatsApp Status ads and other placements when Meta served there) —
 * and Refresh re-reads them with the reading. See `MetaAdResults`. The drafts
 * list is Reach's page, `/reach/ads`, since slice 65 split the ads from the
 * campaigns, so "back" and a discard go there.
 *
 * Slice 52 made "has delivered" Meta's answer rather than Wend's record, so
 * the controls' own reading is handed up here and passed down to the results:
 * one read of Meta for the page, and the two sections cannot disagree about
 * whether this campaign has ever run.
 */
import { Badge, Button, Card, CardContent, Spinner, StateChip, buttonVariants, useToast } from '@wend/ui';
import { ArrowLeft, ExternalLink, Pencil, Send, Trash2, Wrench } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { META_BUDGET_LABELS, type MetaAdDelivery, type MetaAdDraftRecord, type MetaAdsIntegration } from '@wend/shared';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import {
  discardMetaAdDraft,
  fetchMetaAdDraft,
  fetchMetaAdsIntegration,
  publishMetaAdDraft,
} from '../../lib/api/meta-ads';
import { pluralise } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';
import { AdvancedDisclosure } from '../integrations/AdvancedDisclosure';
import { formatMetaMoney } from '../integrations/MetaAdsAccountRead';
import { RequestFailureAlert, requestFailure, type RequestFailure } from '../integrations/request-failure';
import { adsManagerUrl } from './meta-ad-controls';
import { MetaAdControls } from './MetaAdControls';
import { MetaAdResults } from './MetaAdResults';
import { Fact, MetaAdDraftSummary, Mono, isCorrectable, isEditable, isPublishable } from './MetaAdDraftSummary';

export function MetaAdDraftPage() {
  const { draftId = '' } = useParams<{ draftId: string }>();
  const loaded = useAsyncData(async (signal) => {
    const [record, integration] = await Promise.all([
      fetchMetaAdDraft(draftId, signal),
      fetchMetaAdsIntegration(signal),
    ]);
    return { record, integration };
  }, [draftId]);
  const [local, setLocal] = useState<MetaAdDraftRecord | null>(null);
  /* Bumped by the controls' Refresh: the results re-read with the reading above. */
  const [resultsKey, setResultsKey] = useState(0);
  /*
    Meta's reading, made once by the controls and used twice. Slice 52, gap 3:
    the results below are drawn because Meta says the campaign has delivered,
    not because Wend's record says Wend started it — so they need the same
    reading the controls drew, and a second read of the same campaign would
    spend another of the workspace's thirty per five minutes to answer a
    question already answered on screen.
  */
  const [delivery, setDelivery] = useState<MetaAdDelivery | null>(null);

  if (loaded.data === null) {
    return (
      <AppLayout title="Meta ad" actions={<BackToMetaAds />}>
        <Card>
          {loaded.loadingFirst ? (
            <CardContent className="flex items-center gap-3 p-[18px] text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Loading the draft…</span>
            </CardContent>
          ) : (
            <ErrorState error={loaded.error} title="Could not load this draft" onRetry={loaded.reload} />
          )}
        </Card>
      </AppLayout>
    );
  }

  const record = local ?? loaded.data.record;
  const now = Date.now();

  return (
    <AppLayout title={record.draft.campaign.name} actions={<BackToMetaAds />}>
      <div className="flex max-w-4xl flex-col gap-6">
        {record.status === 'published' ? (
          <>
            <MetaAdControls
              record={record}
              integration={loaded.data.integration}
              onChanged={setLocal}
              onRefresh={() => {
                setResultsKey((key) => key + 1);
              }}
              onRead={setDelivery}
            />
            <MetaAdResults record={record} delivery={delivery} reloadKey={resultsKey} />
            {/* What was composed and published, as the record of it — folded,
                in the DOM, and below what the campaign is doing now. */}
            <AdvancedDisclosure label="Draft" summary={pluralise(record.draft.ads.length, 'ad')}>
              <MetaAdDraftSummary record={record} now={now} variant="record" />
            </AdvancedDisclosure>
          </>
        ) : (
          <>
            <MetaAdDraftSummary record={record} now={now} />
            <Actions record={record} integration={loaded.data.integration} now={now} onChanged={setLocal} />
          </>
        )}
      </div>
    </AppLayout>
  );
}

/* The drafts list is Reach's page since slice 65; "Meta ads" is its name in the sidebar. */
function BackToMetaAds() {
  return (
    <Link to="/reach/ads" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
      <ArrowLeft aria-hidden="true" className="size-4" />
      All Meta ads
    </Link>
  );
}

function Actions({
  record,
  integration,
  now,
  onChanged,
}: {
  record: MetaAdDraftRecord;
  integration: MetaAdsIntegration | null;
  now: number;
  onChanged: (record: MetaAdDraftRecord) => void;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [confirming, setConfirming] = useState<'publish' | 'discard' | null>(null);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);

  const { draft, published } = record;
  const money = formatMetaMoney(draft.adSet.budget.amount, record.currency);
  const total = draft.ads.length;
  const created = published.adIds.filter((one) => one !== null).length;
  const resuming = published.campaignId !== null;
  /* A resume can only continue in the account its campaign is in. */
  const wrongAccount =
    integration !== null && published.adAccountId !== null && published.adAccountId !== integration.adAccountId;

  async function publish(): Promise<void> {
    if (pending) return;
    setFailure(null);
    setPending(true);
    try {
      const updated = await publishMetaAdDraft(record.id, record.version);
      onChanged(updated);
      setConfirming(null);
      /* The badge's own words; the reason, when there is one, is printed on
         the page already, once. */
      toast(
        updated.status === 'published'
          ? { tone: 'success', title: 'Published paused' }
          : { tone: 'error', title: 'Publish stopped' },
      );
    } catch (thrown) {
      setFailure(requestFailure(thrown));
    } finally {
      setPending(false);
    }
  }

  async function discard(): Promise<void> {
    if (pending) return;
    setFailure(null);
    setPending(true);
    try {
      await discardMetaAdDraft(record.id);
      /* Replaced, so Back does not land on a draft that no longer exists. */
      void navigate('/reach/ads', { replace: true });
    } catch (thrown) {
      setFailure(requestFailure(thrown));
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col gap-3" data-role="meta-ad-draft-actions">
      {failure !== null && <RequestFailureAlert failure={failure} />}

      {confirming === 'publish' ? (
        <div data-role="meta-ad-publish-confirm" className="flex flex-col gap-4 rounded-base border border-line p-4">
          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
            {/* The money screens' own table — see the summary below, which
                draws the same figure on this same page. */}
            <Fact label={META_BUDGET_LABELS.ad_set[draft.adSet.budget.kind]}>
              <Mono>{money ?? record.currency}</Mono>
            </Fact>
            <Fact label="Ends">
              {draft.adSet.endsOn === null ? <Badge tone="neutral">No end date</Badge> : <Mono>{draft.adSet.endsOn}</Mono>}
            </Fact>
            <Fact label="Ads to create">
              <Mono>{resuming ? `${String(total - created)} of ${String(total)}` : String(total)}</Mono>
            </Fact>
            <Fact label="Ad account">
              {integration === null ? (
                <StateChip state="not_set_up" label="None connected" />
              ) : wrongAccount ? (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <Mono>{published.adAccountId}</Mono>
                  <Badge tone="bad">Not connected</Badge>
                </span>
              ) : (
                integration.accountName ?? <Mono>{integration.adAccountId}</Mono>
              )}
            </Fact>
          </dl>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              loading={pending}
              disabled={integration === null || wrongAccount}
              onClick={() => {
                void publish();
              }}
            >
              <Send aria-hidden="true" className="size-4" />
              {pending ? 'Publishing' : 'Publish to Meta, paused'}
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={() => setConfirming(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : confirming === 'discard' ? (
        <div className="flex flex-wrap items-center gap-3 rounded-base border border-bad/25 p-4">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={pending}
            className="border-bad/45 text-bad hover:border-bad/70 hover:bg-bad/10"
            onClick={() => {
              void discard();
            }}
          >
            <Trash2 aria-hidden="true" className="size-4" />
            Yes, discard this draft
          </Button>
          <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={() => setConfirming(null)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          {isPublishable(record, now) && (
            <Button type="button" size="sm" onClick={() => setConfirming('publish')}>
              <Send aria-hidden="true" className="size-4" />
              {record.status === 'draft' ? 'Publish' : 'Publish again'}
            </Button>
          )}
          {isEditable(record, now) && (
            <Link
              to={`/reach/ads/${record.id}/edit`}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              <Pencil aria-hidden="true" className="size-4" />
              Edit
            </Link>
          )}
          {/* Slice 52, gap 1. The same route: the composer reads `published`
              and decides what it may open, so there is one composer and not
              two. A different word, because Edit on this page has always meant
              a draft with nothing at Meta. */}
          {isCorrectable(record, now) && (
            <Link
              to={`/reach/ads/${record.id}/edit`}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              <Wrench aria-hidden="true" className="size-4" />
              Correct
            </Link>
          )}
          {isEditable(record, now) && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming('discard')}>
              <Trash2 aria-hidden="true" className="size-4" />
              Discard
            </Button>
          )}
          {/* The door for somebody who would rather delete the campaign at
              Meta than correct the draft — the one thing Wend will not do for
              them, because it is irreversible and the row is its only record.
              Offered exactly where Discard is withdrawn. */}
          {isCorrectable(record, now) && published.adAccountId !== null && (
            <a
              href={adsManagerUrl(published.adAccountId, published.campaignId)}
              target="_blank"
              rel="noreferrer"
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              <ExternalLink aria-hidden="true" className="size-4" />
              Ads Manager
            </a>
          )}
        </div>
      )}
    </div>
  );
}
